/* eslint-disable @typescript-eslint/no-empty-function */
import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnDestroy,
   OnInit,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Params } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { ReportDefinitions } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { ConfirmationDialogType } from 'src/app/common-components/confirmation-dialog/models/confirmation-dialog.config';
import * as RoleDetailActions from 'src/app/pages/role-details/state/role-detail.actions';
import { GetUserDetail_user_rolesConnection_items } from '../user-details/queries/generated/GetUserDetail';
import { RoleReportViewModel } from './models/report-permission.model';
import { UserReportViewModel } from './models/user-report-permission.model';

@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   encapsulation: ViewEncapsulation.None,
   selector: 'ndt-report-permissions-list-view',
   styleUrls: ['./report-permissions-list-view.component.scss'],
   templateUrl: './report-permissions-list-view.component.html',
   standalone: false,
})
export class ReportPermissionsListViewComponent implements ControlValueAccessor, OnInit, OnDestroy {
   @Input() roleReports: RoleReportViewModel[] | null;

   @Input() userReports: UserReportViewModel[] | null;

   @Input() isOnSystemPage: boolean;

   @Input() get userRoles(): GetUserDetail_user_rolesConnection_items[] {
      return this._userRoles;
   }

   set userRoles(roles: GetUserDetail_user_rolesConnection_items[]) {
      if (roles) {
         this._userRoles = roles;
         const reportsFromArrays = this._userRoles.map((role) => {
            return role.role.roleReportsConnection.items;
         });

         this.reportsFromRoles = reportsFromArrays
            .reduce((acc, value) => {
               return acc.concat(value);
            }, [])
            .filter((v) => v.report.reportDefinition != null)
            .map((v) => v.report.reportDefinition!);
      }
   }

   @Output() reportsChange: EventEmitter<{ added: string[]; removed: string[] }> = new EventEmitter();

   @Input() isForUserReports?: boolean;

   get isAllSelected(): boolean {
      const selectedReports = Object.values(this.entityReportsMap);
      return Object.values(ReportDefinitions).length === selectedReports.length && selectedReports.every((v) => v);
   }

   private activeOnlyReports: ReportDefinitions[] = [
      ReportDefinitions.ActiveExtensions,
      ReportDefinitions.ExtensionStatistics,
   ];

   private systemOnlyReports: ReportDefinitions[] = [ReportDefinitions.SystemInternationalCalls];

   allReports: string[] = [];

   reportsToDisplay: string[] = [];

   searchText = '';

   queryParams: Params;

   entityReportsMap: { [reportId: string]: boolean } = {};

   currentReports: string[] = [];

   addedReportIds: string[] = [];

   removedReportIds: string[] = [];

   disabled: boolean;

   reportsFromRoles: ReportDefinitions[];

   private dialogIsOpen: boolean;

   private _userRoles: GetUserDetail_user_rolesConnection_items[];

   private destroyed$ = new Subject<void>();

   private onTouch: () => void;

   private onChange: (value: { added: string[]; removed: string[] }) => void;

   constructor(
      private changeDetectorRef: ChangeDetectorRef,
      control: NgControl,
      private store: Store,
      private confirmationDialogService: ConfirmationDialogService
   ) {
      control.valueAccessor = this;
   }

   ngOnInit(): void {

      this.allReports = Object.keys(ReportDefinitions)
         .map((key) => ReportDefinitions[key])
         .filter((report) => {
            if (this.activeOnlyReports.includes(report)) {
               return false;
            }
            if (this.systemOnlyReports.includes(report) && !this.isOnSystemPage) {
               return false;
            }

            return true;
         });

      this.reportsToDisplay = [...this.allReports];

      const reports = this.isForUserReports ? this.userReports : this.roleReports;

      if (reports) {
         reports.forEach((report) => {
            if (!this.currentReports.includes(report.reportId)) {
               this.currentReports.push(report.reportId);
            }

            this.entityReportsMap[report.reportId] = true;
         });
      }

      this.store
         .pipe(select(RoleDetailActions.saveRoleDetailSuccessForNotify), takeUntil(this.destroyed$))
         .subscribe(() => {
            this.addedReportIds = [];
            this.removedReportIds = [];
         });
   }
   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   registerOnChange(fn: (value: { added: string[]; removed: string[] }) => void): void {
      this.onChange = fn;
   }

   onSelectAllClicked(event: MatCheckboxChange): void {
      this.dialogIsOpen = event.checked;
      this.allReports.forEach((report) => {
         this.entityReportsMap[report] = event.checked;
         const eventObj = { event, report };
         this.onCheckboxChange(eventObj, true);
      });
      this.dialogIsOpen = false;
   }

   writeValue(obj: (RoleReportViewModel[] | UserReportViewModel[]) | null): void {
      this.currentReports = [];

      if (obj && obj.length) {
         obj.forEach((report) => {
            this.currentReports.push(report.reportId);
            this.entityReportsMap[report.reportId] = true;
         });

         Object.keys(this.entityReportsMap).forEach((reportId) => {
            if (!obj.some((report) => report.reportId === reportId)) {
               this.entityReportsMap[reportId] = false;
            }
         });
      }
      if (obj && obj.length === 0) {
         this.entityReportsMap = {};
      }
   }

   registerOnTouched(fn: any): void {
      this.onTouch = fn;
   }

   setDisabledState(disabled: boolean) {
      this.disabled = disabled;
   }

   hasReportFromRoles(report: ReportDefinitions) {
      return this.reportsFromRoles?.find((r) => r === report) !== undefined;
   }

   onSearchTextChange(): void {
      if (this.searchText.replace(/\\/g, '').trim() !== '') {
         this.reportsToDisplay = this.allReports.filter((report) => {
            return report.toLowerCase().includes(this.searchText.replace(/\\/g, '').toLowerCase());
         });
      } else {
         this.reportsToDisplay = [...this.allReports];
      }
   }

   onCheckboxChange({ event, report }: { event: MatCheckboxChange; report: string }, isSelectAll = false): void {
      if (this.hasReportFromRoles(ReportDefinitions[report]) && !this.dialogIsOpen) {
         this.openDialog(ReportDefinitions[report], isSelectAll);
         if (!isSelectAll) {
            event.source.checked = true;
         }
         return;
      }

      let foundIndex = -1;

      if (this.isForUserReports === true) {
         foundIndex =
            this.userReports?.findIndex((item) => {
               return item.reportId.toString() === report;
            }) ?? -1;
      } else {
         foundIndex =
            this.roleReports?.findIndex((item) => {
               return item.reportId.toString() === report;
            }) ?? -1;
      }

      let entityReportId = '';
      const roleReport = this.roleReports?.[foundIndex];
      const userReport = this.userReports?.[foundIndex];
      const currentReportsCopy = this.currentReports;

      if (roleReport) {
         entityReportId = roleReport.roleReportId.toString();
      } else if (userReport && this.isForUserReports === true) {
         entityReportId = userReport.userReportId.toString();
      }

      if (event.checked) {
         this.entityReportsMap[report] = true;
         if (!this.currentReports.includes(report) && !this.addedReportIds.includes(report)) {
            this.addReport(report);
            this.emitChanges();
         }
         if (this.removedReportIds.includes(entityReportId)) {
            const indexToRemove = this.removedReportIds.indexOf(entityReportId);
            this.removedReportIds.splice(indexToRemove, 1);
            this.emitChanges();
         }
      } else {
         this.entityReportsMap[report] = false;
         if (this.currentReports.includes(report) && !this.removedReportIds.includes(entityReportId)) {
            this.removeReport(report);
            this.emitChanges();
         }
         if (this.addedReportIds.includes(report)) {
            const indexToRemove = this.addedReportIds.indexOf(report);
            this.addedReportIds.splice(indexToRemove, 1);
            this.emitChanges();
         }
      }

      this.currentReports = currentReportsCopy;
   }

   private addReport(report: string): void {
      const currentReportsCopy = [...this.currentReports];

      const index = this.allReports.indexOf(report);
      if (index !== -1 && !this.currentReports.includes(report) && !this.addedReportIds.includes(report)) {
         this.addedReportIds.push(report);
         this.emitChanges();
      }

      this.currentReports = currentReportsCopy;

      this.getControlValue();
   }

   private removeReport(report: string): void {
      const currentReportsCopy = [...this.currentReports];

      if (currentReportsCopy.includes(report) && !this.removedReportIds.includes(report)) {
         let foundIndex = -1;
         if (this.isForUserReports) {
            foundIndex =
               this.userReports?.findIndex((item) => {
                  return item.reportId.toString() === report;
               }) ?? -1;
         } else {
            foundIndex =
               this.roleReports?.findIndex((item) => {
                  return item.reportId.toString() === report;
               }) ?? -1;
         }

         if (this.isForUserReports && foundIndex !== -1 && this.userReports) {
            this.removedReportIds.push(this.userReports[foundIndex].userReportId.toString());
         }

         if (!this.isForUserReports && foundIndex !== -1 && this.roleReports) {
            this.removedReportIds.push(this.roleReports[foundIndex].roleReportId.toString());
         }

         this.emitChanges();
      }

      this.currentReports = currentReportsCopy;

      this.getControlValue();
   }

   private emitChanges(): void {
      const addedReportIdsAsString = this.addedReportIds.map(
         (id) => this.getReportDefinitionId(ReportDefinitions[id])?.toString() ?? ''
      );
      const removedReportIdsAsString = this.removedReportIds.map(String);

      this.reportsChange.emit({ added: addedReportIdsAsString, removed: removedReportIdsAsString });
   }

   private getControlValue(): { added: string[]; removed: string[] } {
      return {
         added: this.addedReportIds,
         removed: this.removedReportIds,
      };
   }

   private openDialog(report: ReportDefinitions, isSelectAll: boolean) {
      this.dialogIsOpen = true;
      let message: string;
      if (isSelectAll) {
         message =
            'Some privileges selected are granted by roles. User must be removed from the role to remove privileges. \n \n Select individual privileges to view corresponding role(s).';
      } else {
         const rolesListString = this.userRoles
            .filter((role) => role.role.roleReportsConnection.items.find((r) => r.report.reportDefinition === report))
            .map((v) => v.role.name);
         message = `This privilege is granted by a role. User must be removed from the following role(s) to remove privilege: \n ${rolesListString}`;
      }
      const dialogRef = this.confirmationDialogService.confirm({
         data: {
            dialogType: ConfirmationDialogType.Ok,
            header: 'Could not remove privilege',
            message,
         },
         size: DialogSize.Medium,
      });

      dialogRef.afterClosed().subscribe(() => {
         this.dialogIsOpen = false;
      });
   }

   private getReportDefinitionId(definition: ReportDefinitions): number | null {
      switch (definition) {
         case ReportDefinitions.ExtensionDetail:
            return 1;
         case ReportDefinitions.ExtensionSummary:
            return 2;
         case ReportDefinitions.HourlyCallVolume:
            return 3;
         case ReportDefinitions.TopCallers:
            return 4;
         case ReportDefinitions.DailyCallVolume:
            return 5;
         case ReportDefinitions.WeekDayCallVolume:
            return 6;
         case ReportDefinitions.MissedCalls:
            return 7;
         case ReportDefinitions.InboundCallsByPhoneNumber:
            return 8;
         case ReportDefinitions.AbandonedCalls:
            return 9;
         case ReportDefinitions.InternationalCalls:
            return 10;
         case ReportDefinitions.HuntGroupExtension:
            return 11;
         case ReportDefinitions.ActiveExtensions:
            return 12;
         case ReportDefinitions.ExtensionStatistics:
            return 13;
         case ReportDefinitions.SystemInternationalCalls:
            return 14;
         default:
            return null;
      }
   }
}
